<template>
	<div class="announcement-details">
		<div class="announcement-header">
			<CMobileSearch
				:value.sync="searchValue"
				:placeholder="$t('enterprise.default-search')"
				@change="changeSearch"
			/>

			<m-inline-dropdown
				class="dropdown"
				:value.sync="activeType"
				:options="typeRange"
				position="right"
				:defaultText="$t('enterprise.type')"
				@change="value => handleChangeDropDown(value, 'type')"
			>
				<template v-slot:title="props">
					<span class="dropdown-menu-text">{{ props.actived.text }}</span>
					<span class="dropdown-angle-icon"></span>
				</template>
			</m-inline-dropdown>
		</div>

		<div class="announcement-overview">
			<CMobileScroll
				ref="scrollRef"
				pullDown
				@pull-down="onPullDown"
				pullUp
				:moreData="hasMoreData"
				@pull-up="onPullUp"
				v-loading="isLoading"
			>
				<div class="announcement-list">
					<MobDocumentCard v-if="finalData.length" :listData="finalData" @goDetail="handlePreview" />
				</div>
				<Empty v-if="isEmpty" />
			</CMobileScroll>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, computed, getCurrentInstance } from 'vue';
import { useCurrentContext } from '@rebuild/materials/shared/announcement/context';
import MobDocumentCard from '@rebuild/materials/shared/common/MobDocumentCard.vue';
import CMobileSearch from '@/components/mobile/CMobileSearch.vue';
import CMobileScroll from '@/components/mobile/CMobileScroll.vue';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useModuleNavigation } from '@rebuild/hooks/useModuleNavigation';
import { usePaginationMobile } from '@rebuild/hooks/load-more/usePaginationMobile';
import { useCommonData } from '@/hooks';
import { dateFormatYearMonthDate } from '@/utils/format';

const RESEARCH_TAB_VALUE = '__research__';

const { proxy } = getCurrentInstance();
const { announcement, research } = useModuleData();
const { viewModule } = useModuleNavigation();

const context = useCurrentContext();
const languageType = computed(() => context.languageType);

const { isFlowTmpl, isFlowXTmpl } = useCommonData();

const searchValue = ref('');
const activeType = ref('');

const announcementTabs = computed(() => [
	{ text: proxy.$t('enterprise.announcement.all'), value: '' },
	{ text: proxy.$t('enterprise.regular-announcement'), value: 'regular' },
	{ text: proxy.$t('enterprise.interim-announcement'), value: 'temporarily' }
]);

const researchTab = { text: '调研记录', value: RESEARCH_TAB_VALUE };

const typeRange = computed(() => [...announcementTabs.value, researchTab]);

const isResearchTab = computed(() => activeType.value === RESEARCH_TAB_VALUE);

const isLoading = computed(() => isResearchTab.value ? research.loading : announcement.loading);
const isEmpty = computed(() => finalData.value.length === 0 && !isLoading.value);

const { currentPage, pageSize, finalData, setData, hasMoreData, onPullDown, onPullUp } = usePaginationMobile({
	bindChange: handlePaginationChange
});

const handleChangeDropDown = (item, type) => {
	if (type === 'type') {
		activeType.value = item.value;
	}
	refreshData();
};

const changeSearch = () => {
	refreshData();
};

async function handlePaginationChange() {
	if (isResearchTab.value) {
		await research.refresh({
			pageStart: currentPage.value - 1,
			pageNum: pageSize.value
		});
		const data = research.data?.rows || [];
		const total = research.data?.total || 0;
		setData(data.map(item => ({
			...item,
			time: dateFormatYearMonthDate(item.ptime),
			classificationInfo: ''
		})), total);
	} else {
		const params = {
			useStockCode: isFlowTmpl.value || isFlowXTmpl.value,
			classificationIds: activeType.value,
			pageStart: currentPage.value - 1,
			pageNum: pageSize.value,
			order: 'desc',
			title: searchValue.value,
			...languageType.value
		};
		await announcement.refresh(params);
		const data = announcement.data?.data || announcement.data?.rows || [];
		const total = announcement.data?.total || 0;
		setData(data.map(item => ({
			...item,
			time: item.publishDate,
			classificationInfo: ''
		})), total);
	}
}

const refreshData = () => {
	currentPage.value = 1;
	handlePaginationChange();
};

const handlePreview = info => {
	viewModule(isResearchTab.value ? 'research' : 'announcement', info);
};

onMounted(() => {
	refreshData();
});
</script>

<style lang="less" scoped>
.announcement-details {
	height: 100%;

	.announcement-overview {
		height: calc(100% - 0.44rem);
		.announcement-list {
			padding: 10px 16px;
		}
	}
}

.announcement-header {
	display: flex;
	padding-right: 16px;
	.dropdown + .dropdown {
		margin-left: 16px;
	}
}

.dropdown-menu-text {
	font-size: 14px;
	font-weight: 400;
	line-height: 18px;
}
</style>
